import { Suspense, useEffect } from 'react';
import * as Sentry from '@sentry/react';
import { BrowserRouter, Routes } from 'react-router-dom';
import { MatomoTracker } from '@/components/MatomoTracker';
import { QueryClientProvider } from '@tanstack/react-query';
import { queryClient } from './lib/query-client';
import { PatientAuthProvider } from '@/contexts/PatientAuthContext';
import { SettingsProvider } from '@/contexts/SettingsContext';
import { SyncStatusIndicator } from '@/components/SyncStatusIndicator';
import { patientRoutes } from '@/app/routes/patientRoutes';
import { renderRoutes } from '@/app/routes/renderRoutes';
import './App.css';

function App() {
  // ============================================================================
  // Migration: Clean up old survey_draft_* localStorage (one-time)
  // ============================================================================
  useEffect(() => {
    const migrationKey = 'survey_draft_migrated_v1';
    const migrated = localStorage.getItem(migrationKey);

    if (!migrated) {
      console.log('[Migration] Cleaning up old survey drafts from localStorage');

      let removedCount = 0;
      const keysToRemove: string[] = [];

      // Find all survey_draft_* keys
      for (let i = 0; i < localStorage.length; i++) {
        const key = localStorage.key(i);
        if (key && key.startsWith('survey_draft_')) {
          keysToRemove.push(key);
        }
      }

      // Remove them
      keysToRemove.forEach(key => {
        localStorage.removeItem(key);
        removedCount++;
      });

      // Mark migration as complete
      localStorage.setItem(migrationKey, 'true');

      if (removedCount > 0) {
        console.log(`[Migration] Removed ${removedCount} old survey draft(s)`);
      } else {
        console.log('[Migration] No old survey drafts found');
      }
    }
  }, []);

  return (
    <Sentry.ErrorBoundary fallback={<div style={{display:'flex',height:'100vh',alignItems:'center',justifyContent:'center',color:'#666'}}>오류가 발생했습니다. 페이지를 새로고침해주세요.</div>}>
      <QueryClientProvider client={queryClient}>
        <SettingsProvider>
          <PatientAuthProvider>
            <BrowserRouter>
              <MatomoTracker />
              <Suspense fallback={null}>
                <Routes>{renderRoutes(patientRoutes)}</Routes>
              </Suspense>
              <SyncStatusIndicator />
            </BrowserRouter>
          </PatientAuthProvider>
        </SettingsProvider>
      </QueryClientProvider>
    </Sentry.ErrorBoundary>
  );
}

export default App;
